import FilterListIcon from '@mui/icons-material/FilterList';
import { Box, Button, Chip, Popover, Stack, Typography } from '@mui/material';
import React from 'react';
import { type SearchCriteria, TFilterKey, type TFilterLabels } from '../types';

import tokens from '@verifime/design-tokens';
import SearchInput from './SearchInput';
import { useSearch, UseSearchProp } from './hook/useSearch';
import { FilterPanelProps } from './filters/types';

export interface TSearchCriteriaProps<T> extends UseSearchProp<T> {
  filterLabels: TFilterLabels<T>;
  FilterPanel: React.FC<FilterPanelProps<T>>;
  placeholder?: string;
  onSearch: (criteria: SearchCriteria<T>) => Promise<void>;
}

export default function SearchCriteria<T>({
  createEmptyCriteria,
  filterLabels,
  FilterPanel,
  placeholder,
  onSearch,
  onCriteriaChanged,
}: TSearchCriteriaProps<T>) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [loading, setLoading] = React.useState(false);
  const searchState = useSearch<T>({ createEmptyCriteria, onCriteriaChanged });

  const showFilterPanel = Boolean(anchorEl);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    onSearch({
      searchText: searchState.searchText,
      filters: searchState.filters,
    }).finally(() => {
      setLoading(false);
    });
  };

  const handleApplyFilters = (data: T) => {
    searchState.setFilters(data);
    setAnchorEl(null);
  };

  const handleOpenFilters = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseFilters = () => {
    setAnchorEl(null);
  };

  return (
    <Stack component="form" onSubmit={handleSubmit} data-component="SearchCriteria">
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: tokens.spacingBase }}>
        <SearchInput
          value={searchState.searchText}
          onTextChange={searchState.updateSearchText}
          placeholder={placeholder}
        />
        {FilterPanel && (
          <Button
            variant="text"
            size="large"
            onClick={handleOpenFilters}
            data-action="show-search-filters"
            disabled={showFilterPanel}
          >
            <FilterListIcon /> Filters
          </Button>
        )}
        <Button
          type="submit"
          variant="contained"
          disabled={!!showFilterPanel || loading}
          size="large"
          data-action="search"
        >
          {loading ? 'Searching...' : 'Search'}
        </Button>
      </Box>

      <Stack
        direction="row"
        gap={tokens.spacingBase}
        sx={{ mt: tokens.spacingBase }}
        data-component="AppliedSearchFilters"
      >
        {Object.entries(searchState.filters).map(([key, value]) => {
          const filterKey = key as TFilterKey<T>;
          const chipLabel = (
            <Stack direction="row" spacing={tokens.spacingSm} alignItems="center">
              <Typography variant="button">{filterLabels[filterKey] || key}</Typography>
              <Typography variant="body2">{value}</Typography>
            </Stack>
          );
          return <Chip key={String(key)} label={chipLabel} />;
        })}
      </Stack>

      <Popover
        open={showFilterPanel}
        anchorEl={anchorEl}
        onClose={handleCloseFilters}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: tokens.spacingXs,
              minWidth: 400,
            },
          },
        }}
      >
        {showFilterPanel && (
          <FilterPanel
            defaultValues={searchState.filters}
            onApply={(data) => {
              handleApplyFilters(data);
            }}
            onCancel={handleCloseFilters}
          />
        )}
      </Popover>
    </Stack>
  );
}
